"use client";

import { useEffect } from "react";
import { Search as SearchIcon } from "lucide-react";
import EditableText from "@/components/edit-mode/EditableText";

interface SearchToggleProps {
  open: boolean;
  onToggle: () => void;
  compact: boolean;
  label: string;
}

export function SearchToggle({ open, onToggle, compact, label }: SearchToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={open ? "Close search" : label}
      aria-expanded={open}
      className="text-foreground hover:text-foreground/60 group relative -m-2 inline-flex cursor-pointer items-center gap-2 p-2 transition-colors"
    >
      <SearchIcon
        size={compact ? 16 : 18}
        strokeWidth={1.5}
        className="transition-transform group-hover:rotate-12"
      />
      {!compact && (
        <span className="text-foreground/40 group-hover:text-foreground/70 hidden font-mono text-[10px] font-bold tracking-[0.2em] uppercase transition-colors sm:inline">
          <EditableText section="header" field="searchText" value={label} />
        </span>
      )}
    </button>
  );
}

export function useKeyboardShortcuts(onOpen: () => void) {
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (inField) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpen();
      } else if (e.key === "/") {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [onOpen]);
}
